import { ref } from 'vue'

// Define the window interface with dataLayer
declare global {
  interface Window {
    dataLayer?: any[]
  }
}

// Define event types for type safety
export interface GAEvent {
  event: string
  category?: string
  action?: string
  label?: string
  value?: number
  non_interaction?: boolean
  [key: string]: any // Allow additional custom properties
}

// Track whether analytics is initialized
const isInitialized = ref(false)

// Store the last sent event for debugging and testing
const lastEvent = ref<GAEvent | null>(null)

// Track all events for debugging
const eventHistory = ref<GAEvent[]>([])

// Initialize the data layer
export function initializeAnalytics(): void {
  if (typeof window !== 'undefined' && !isInitialized.value) {
    if (!window.dataLayer) {
      window.dataLayer = []
    }
    isInitialized.value = true
  }
}

/**
 * Push an event to the data layer
 * @param eventData Event data to push
 */
export function pushEvent(eventData: GAEvent): void {
  if (typeof window !== 'undefined' && window.dataLayer) {
    try {
      window.dataLayer.push(eventData)
      lastEvent.value = eventData
      eventHistory.value.push(eventData)
    } catch (error) {
      console.error('Failed to push event to dataLayer:', error)
    }
  }
}

/**
 * Track a page view
 * @param path Page path
 * @param title Optional page title
 */
export function trackPageView(path: string, title?: string): void {
  pushEvent({
    event: 'page_view',
    page_path: path,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
  })
}

/**
 * Set the user ID for analytics
 * @param userId User's Auth0 ID
 */
export function setUserId(userId: string): void {
  pushEvent({
    event: 'set_user_id',
    user_id: userId,
  })
}

/**
 * Track a button click
 * @param buttonId Button identifier
 * @param buttonText Button text or description
 * @param additionalData Any additional data to include
 * @param category Optional category for the event
 */
export function trackButtonClick(
  buttonId: string,
  buttonText: string,
  additionalData?: Record<string, any>,
  category = 'UI Interaction',
): void {
  pushEvent({
    event: 'button_click',
    category: category,
    action: 'Button Click',
    label: buttonText,
    button_id: buttonId,
    ...additionalData,
  })
}

/**
 * Track a form submission
 * @param formId Form identifier
 * @param formData Form data or description
 */
export function trackFormSubmission(formId: string, formData?: Record<string, any>): void {
  pushEvent({
    event: 'form_submit',
    category: 'Form',
    action: 'Submit',
    form_id: formId,
    ...formData,
  })
}

/**
 * Track a conversion event (sign-up, purchase, etc.)
 * @param conversionType Type of conversion
 * @param value Optional monetary value
 * @param additionalData Any additional data to include
 */
export function trackConversion(
  conversionType: string,
  value?: number,
  additionalData?: Record<string, any>,
): void {
  pushEvent({
    event: 'conversion',
    category: 'Conversion',
    action: conversionType,
    value,
    ...additionalData,
  })
}

/**
 * Clear user ID (for logout)
 */
export function clearUserId(): void {
  pushEvent({
    event: 'clear_user_id',
  })
}

/**
 * Debug helper to check analytics state in browser console
 */
export function debugAnalytics(): {
  isInitialized: boolean
  lastEvent: GAEvent | null
  eventHistory: GAEvent[]
  dataLayerExists: boolean
  dataLayerLength: number
} {
  return {
    isInitialized: isInitialized.value,
    lastEvent: lastEvent.value,
    eventHistory: eventHistory.value,
    dataLayerExists: typeof window !== 'undefined' && !!window.dataLayer,
    dataLayerLength:
      typeof window !== 'undefined' && window.dataLayer ? window.dataLayer.length : 0,
  }
}

// In development, expose debug function to window for console testing
if (import.meta.env.DEV) {
  if (typeof window !== 'undefined') {
    ;(window as any).__analyticsDebug = debugAnalytics
  }
}

// Export readonly versions of reactive state
export { isInitialized, lastEvent, eventHistory }
